/**
 * THE SOUND REGISTRY — the one place you add or re-skin audio.
 *
 * SFX and ambience beds have a `synth` spec and may be replaced by a file URL.
 * Music is authored-file-only and streams directly instead of using SoundBank.
 * This is the extensibility seam: designers work here, not in game systems.
 *
 *   SOUNDS  — one-shot SFX (combat, movement, UI). Optionally spatial.
 *   BEDS    — looping ambience whose gain is automated from game state.
 *   MUSIC   — authored streamed tracks selected by high-level game state.
 *
 * Spatial sounds are positioned in SYSTEM space by the caller; the facade
 * rebases them by the floating origin before they hit the panner.
 */
import type { SynthSpec } from "./synth";
import type { BusName } from "./engine";
import { FOOTSTEP_SURFACES, FOOTSTEP_VARIANTS, footstepId, footstepSynth, landingId, landingSynth } from './footsteps';

export interface SfxDef {
  synth: SynthSpec;
  url?: string;
  bus?: BusName; // default "sfx"
  gain?: number; // playback gain multiplier (default 1)
  rate?: number; // base playbackRate (default 1)
  rateVar?: number; // ± random rate spread, 0..1 (pitch variety)
  maxVoices?: number; // concurrent cap for this id (default 6)
  voiceGroup?: string; // optional shared voice cap across related variants
  cooldownMs?: number; // ignore re-triggers within this window (default 0)
  spatial?: boolean; // position in 3D (default false → 2D)
  refDist?: number; // panner reference distance (m)
  maxDist?: number; // beyond this, culled / silent (m)
  rolloff?: number; // inverse-distance rolloff factor
}

export interface BedDef {
  synth: SynthSpec; // must be loop:true
  url?: string;
  bus?: BusName; // default "ambient"
  gain?: number; // ceiling gain (the bed manager scales 0..this)
  spatial?: boolean; // positional bed (e.g. the mining beam, ship boosters)
  refDist?: number; // panner reference distance (m) — full level within this
  maxDist?: number; // beyond this, effectively silent / culled (m)
  rolloff?: number; // inverse-distance rolloff factor
}

export interface MusicTrackDef {
  /** Music must be an authored file. Procedural synthesis is deliberately not
   * supported for music; synth remains available for SFX and ambience. */
  url: string;
  loop?: boolean; // loop the stream + use the play window (default: play once)
  gain?: number;
}

/** A context's music behaves like Minecraft's: pick a random track from the
 *  pool, play it once, then stay SILENT for a random gap before the next. NOT
 *  a continuous bed. Variety comes from a pool holding several distinct tracks. */
export interface MusicPool {
  tracks: string[]; // ids into MUSIC_TRACKS (random pick, no immediate repeat)
  startGap: number; // silence after entering the context before the first track (s)
  minGap: number; // silent gap between tracks (s)
  maxGap: number;
  minPlay: number; // how long a track plays before it fades out (s)
  maxPlay: number;
}

// ----------------------------------------------------------------------------
// One-shot SFX
// ----------------------------------------------------------------------------
function dialogueSample(freq: number, freqEnd: number, type: OscillatorType, gain: number, dur: number, bold = false): SfxDef {
  const tone: SynthSpec = { kind: "tone", dur, freq, freqEnd, type,
    attack: bold ? 0.0015 : 0.003, hold: bold ? 0.008 : 0, decay: bold ? 0.024 : 0.018 };
  return {
    synth: bold ? { kind: "layers", dur, layers: [
      { ...tone, gain: 0.9 },
      { kind: "tone", dur, freq: freq * 2, freqEnd: freqEnd * 2, type: "triangle",
        gain: 0.24, attack: 0.0015, hold: 0.006, decay: 0.022 },
    ] } : tone,
    bus: "sfx", gain, maxVoices: 1, voiceGroup: "dialogue", cooldownMs: 25,
  };
}
export const SOUNDS: Record<string, SfxDef> = {
  ...Object.fromEntries(FOOTSTEP_SURFACES.flatMap(surface =>
    Array.from({ length: FOOTSTEP_VARIANTS }, (_, i) => [landingId(surface, i), {
      synth: landingSynth(surface, i), gain: .30, rateVar: .025,
      maxVoices: 4, voiceGroup: 'landings', cooldownMs: 70,
    } satisfies SfxDef]))),
  ...Object.fromEntries(FOOTSTEP_SURFACES.flatMap(surface =>
    Array.from({ length: FOOTSTEP_VARIANTS }, (_, i) => [footstepId(surface, i), {
      synth: footstepSynth(surface, i), gain: .38, rateVar: .055,
      maxVoices: 4, voiceGroup: 'footsteps', cooldownMs: 70,
    } satisfies SfxDef]))),
  // Short character voice samples, reused per voiced grapheme. A `url` here can
  // replace the generated sample with an authored recording without changing dialogue.
  "dialogue.low": dialogueSample(135, 120, "triangle", 0.18, 0.03),
  "dialogue.warm": dialogueSample(230, 200, "triangle", 0.14, 0.028),
  "dialogue.bright": dialogueSample(340, 370, "sine", 0.12, 0.026),
  "dialogue.low.bold": dialogueSample(135, 115, "triangle", 0.18, 0.038, true),
  "dialogue.warm.bold": dialogueSample(230, 195, "triangle", 0.14, 0.036, true),
  "dialogue.bright.bold": dialogueSample(340, 350, "sine", 0.12, 0.034, true),
  // --- Weapons ---
  "weapon.player.fire": {
    // Softened: lower start pitch (1500→950 Hz) + quieter (0.9→0.6) — the old zap read as shrill/jarring.
    synth: { kind: "noise", dur: 1, color: "brown", freq: 930, q: 0.3, type: "bandpass", gain: 0.366, attack: 0.00512, hold: 0.06, decay: 0.15 },
  },
  "weapon.remote.fire": {
    // Softened to match weapon.player.fire (same shot, another player): lower pitch (1400→900 Hz) +
    // an explicit quieter gain (was defaulting to 1.0 — louder than the player's own shot).
    synth: { kind: "zap", dur: 0.16, freq: 900, freqEnd: 300, type: "sawtooth", gain: 0.5 },
    spatial: true, refDist: 8, maxDist: 450, rolloff: 1.2, gain: 0.55, rateVar: 0.1, maxVoices: 6,
  },
  "weapon.ship.fire": {
    // Softened: quieter (0.8→0.55) + a touch lower (620→540 Hz).
    synth: { kind: "zap", dur: 0.22, freq: 540, freqEnd: 110, type: "square", gain: 0.5 },
    spatial: true, refDist: 60, maxDist: 5000, rolloff: 0.9, gain: 0.55, rateVar: 0.06, cooldownMs: 40,
  },
  "weapon.drone.fire": {
    // Softened: the ASCENDING zap topped out shrill (1700 Hz) — lower the peak (→1150) + an explicit
    // quieter gain (was defaulting to 1.0).
    synth: { kind: "zap", dur: 0.18, freq: 680, freqEnd: 1150, type: "triangle", gain: 0.45 },
    spatial: true, refDist: 10, maxDist: 600, rolloff: 1.1, gain: 0.6, rateVar: 0.12,
  },

  // --- Impacts / explosions ---
  "impact.terrain": {
    synth: { kind: "noise", dur: 0.18, color: "brown", freq: 520, type: "lowpass", decay: 0.16, gain: 0.6 },
    spatial: true, refDist: 6, maxDist: 300, rateVar: 0.15, cooldownMs: 25, maxVoices: 8,
  },
  "impact.body": {
    synth: {
      kind: "layers", dur: 0.2, gain: 0.7, layers: [
        { kind: "thud", dur: 0.16, freq: 150, freqEnd: 70, noise: 0.4 },
        { kind: "noise", dur: 0.12, color: "white", freq: 1800, type: "highpass", decay: 0.1, gain: 0.3 },
      ],
    },
    spatial: true, refDist: 6, maxDist: 350, rateVar: 0.12,
  },
  "hit.marker": {
    // Halo-style hit-confirm CLICK, paired with the reticle hitmarker flash — deliberately
    // SUBTLE, on the UI bus (2D — it's reticle feedback, not a world sound; the impact
    // itself already plays positionally). A dry percussive tick, not a tone: a snappy
    // high-band noise transient over a tiny woody knock (user call 2026-08-14 — the first
    // cut was a triangle "tk" and read as a beep). The cooldown folds same-burst
    // multi-hits into one click instead of a machine-gun clatter.
    synth: {
      kind: "layers", dur: 0.04, gain: 0.5, layers: [
        { kind: "noise", dur: 0.035, color: "white", freq: 3400, q: 1.8, type: "bandpass", attack: 0.001, decay: 0.028, gain: 0.5 },
        { kind: "tone", dur: 0.025, freq: 1150, freqEnd: 900, type: "triangle", attack: 0.001, decay: 0.022, gain: 0.16 },
      ],
    },
    bus: "ui", gain: 0.5, rateVar: 0.06, cooldownMs: 40, maxVoices: 4,
  },
  "vehicle.impact": {
    synth: {
      kind: "layers", dur: 0.42, gain: 0.8, layers: [
        { kind: "thud", dur: 0.38, freq: 92, freqEnd: 36, noise: 0.65 },
        { kind: "noise", dur: 0.24, color: "white", freq: 2400, freqEnd: 700, type: "bandpass", q: 0.7, decay: 0.22, gain: 0.42 },
        { kind: "tone", dur: 0.18, freq: 460, freqEnd: 180, type: "triangle", decay: 0.16, gain: 0.18 },
      ],
    },
    spatial: true, refDist: 10, maxDist: 450, rolloff: 1,
    gain: 0.9, rateVar: 0.1, cooldownMs: 100, maxVoices: 3,
  },
  "explosion.drone": {
    synth: {
      kind: "layers", dur: 0.6, gain: 0.8, layers: [
        { kind: "thud", dur: 0.5, freq: 130, freqEnd: 38, noise: 0.7 },
        { kind: "noise", dur: 0.5, color: "brown", freq: 420, type: "lowpass", attack: 0.005, decay: 0.5, gain: 0.7 },
        { kind: "noise", dur: 0.2, color: "white", freq: 2400, type: "highpass", decay: 0.18, gain: 0.25 },
      ],
    },
    spatial: true, refDist: 14, maxDist: 1500, rolloff: 1, rateVar: 0.1,
  },

  // --- Melee ---
  "melee.swing": {
    synth: { kind: "noise", dur: 0.22, color: "white", freq: 1200, freqEnd: 380, type: "bandpass", q: 1.3, decay: 0.2, gain: 0.4 },
    gain: 0.8, rateVar: 0.12, cooldownMs: 60,
  },
  "melee.swing.blade": {
    synth: {
      kind: "layers", dur: 0.26, gain: 0.45, layers: [
        { kind: "noise", dur: 0.24, color: "white", freq: 2600, freqEnd: 900, type: "bandpass", q: 2, decay: 0.22, gain: 0.5 },
        { kind: "tone", dur: 0.22, freq: 2400, freqEnd: 3200, type: "triangle", attack: 0.005, decay: 0.2, gain: 0.12 },
      ],
    },
    gain: 0.8, rateVar: 0.1, cooldownMs: 60,
  },
  "melee.hit": {
    synth: { kind: "thud", dur: 0.16, freq: 200, freqEnd: 90, noise: 0.5, gain: 0.7 },
    spatial: true, refDist: 6, maxDist: 280, rateVar: 0.14,
  },
  "melee.hit.blade": {
    synth: {
      kind: "layers", dur: 0.3, gain: 0.6, layers: [
        { kind: "tone", dur: 0.28, freq: 1700, type: "triangle", attack: 0.002, decay: 0.26, gain: 0.3, detune: 12 },
        { kind: "noise", dur: 0.12, color: "white", freq: 3200, type: "highpass", decay: 0.1, gain: 0.3 },
      ],
    },
    spatial: true, refDist: 6, maxDist: 300, rateVar: 0.1,
  },
  "combat.block": {
    synth: {
      kind: "layers", dur: 0.22, gain: 0.6, layers: [
        { kind: "tone", dur: 0.2, freq: 760, type: "square", decay: 0.18, gain: 0.25 },
        { kind: "noise", dur: 0.1, color: "white", freq: 2600, type: "highpass", decay: 0.08, gain: 0.3 },
      ],
    },
    gain: 0.85, rateVar: 0.12,
  },
  "combat.parry": {
    synth: { kind: "tone", dur: 0.42, freq: 2100, type: "sine", attack: 0.002, decay: 0.4, gain: 0.3, vibrato: 7, vibratoDepth: 14, detune: 8 },
    gain: 0.85, rateVar: 0.06,
  },
  "player.hurt": {
    synth: {
      kind: "layers", dur: 0.3, gain: 0.7, layers: [
        { kind: "thud", dur: 0.22, freq: 150, freqEnd: 80, noise: 0.3 },
        { kind: "tone", dur: 0.25, freq: 260, freqEnd: 180, type: "sawtooth", decay: 0.22, gain: 0.15 },
      ],
    },
    gain: 0.8, cooldownMs: 250,
  },

  // --- Movement ---
  "jump": {
    synth: {
      kind: "layers", dur: 0.2, gain: 0.4, layers: [
        { kind: "noise", dur: 0.12, color: "white", freq: 1400, type: "bandpass", q: 0.8, decay: 0.1, gain: 0.25 },
      ],
    },
    gain: 0.7, rateVar: 0.08,
  },
  "land.soft": {
    synth: { kind: "thud", dur: 0.14, freq: 150, freqEnd: 70, noise: 0.3, gain: 0.5 },
    gain: 0.7, rateVar: 0.1,
  },

  // --- Homestead building (place/demolish thunks + the weld click) ---
  "build.place": {
    synth: {
      kind: "layers", dur: 0.24, gain: 0.7, layers: [
        { kind: "thud", dur: 0.2, freq: 210, freqEnd: 95, noise: 0.4 },
        { kind: "noise", dur: 0.14, color: "brown", freq: 900, q: 0.6, type: "bandpass", decay: 0.1, gain: 0.35 },
      ],
    },
    gain: 0.8, cooldownMs: 120, rateVar: 0.06,
  },
  "build.demolish": {
    synth: {
      kind: "layers", dur: 0.42, gain: 0.75, layers: [
        { kind: "thud", dur: 0.34, freq: 120, freqEnd: 45, noise: 0.7 },
        { kind: "noise", dur: 0.3, color: "brown", freq: 420, type: "lowpass", decay: 0.26, gain: 0.5 },
      ],
    },
    gain: 0.85, cooldownMs: 150, rateVar: 0.08,
  },
  "build.snap": {
    synth: { kind: "tone", dur: 0.07, freq: 440, freqEnd: 640, type: "triangle", attack: 0.003, decay: 0.06, gain: 0.2 },
    bus: "ui", gain: 0.5, cooldownMs: 90,
  },
  "land.hard": {
    synth: {
      kind: "layers", dur: 0.3, gain: 0.75, layers: [
        { kind: "thud", dur: 0.26, freq: 95, freqEnd: 40, noise: 0.6 },
        { kind: "noise", dur: 0.22, color: "brown", freq: 320, type: "lowpass", decay: 0.2, gain: 0.5 },
      ],
    },
    gain: 0.85, rateVar: 0.08,
  },
  "dodge": {
    synth: { kind: "noise", dur: 0.3, color: "white", freq: 900, freqEnd: 280, type: "bandpass", q: 1.5, attack: 0.01, decay: 0.28, gain: 0.35 },
    gain: 0.7, rateVar: 0.1,
  },

  // --- Footsteps (surface chosen by the facade) ---
  "footstep.default": {
    synth: { kind: "noise", dur: 0.09, color: "brown", freq: 420, type: "lowpass", attack: 0.002, decay: 0.08, gain: 0.5 },
    gain: 0.5, rateVar: 0.18, maxVoices: 5, cooldownMs: 70,
  },
  "footstep.hard": {
    synth: {
      kind: "layers", dur: 0.1, gain: 0.5, layers: [
        { kind: "noise", dur: 0.08, color: "white", freq: 2400, type: "bandpass", q: 2.5, decay: 0.07, gain: 0.4 },
        { kind: "tone", dur: 0.06, freq: 600, type: "triangle", decay: 0.05, gain: 0.12 },
      ],
    },
    gain: 0.45, rateVar: 0.16, maxVoices: 5, cooldownMs: 70,
  },
  "footstep.water": {
    synth: { kind: "noise", dur: 0.16, color: "white", freq: 1600, freqEnd: 700, type: "bandpass", q: 0.9, attack: 0.003, decay: 0.14, gain: 0.4 },
    gain: 0.55, rateVar: 0.2, maxVoices: 5, cooldownMs: 70,
  },

  // --- Water / liquid ---
  "water.splash": {
    synth: { kind: "noise", dur: 0.3, color: "white", freq: 1800, freqEnd: 600, type: "bandpass", q: 0.8, attack: 0.004, decay: 0.28, gain: 0.45 },
    spatial: true, refDist: 6, maxDist: 260, rateVar: 0.18, cooldownMs: 40, maxVoices: 8,
  },
  "water.enter": {
    synth: {
      kind: "layers", dur: 0.45, gain: 0.6, layers: [
        { kind: "noise", dur: 0.4, color: "white", freq: 1500, freqEnd: 450, type: "bandpass", q: 0.7, decay: 0.38, gain: 0.5 },
        { kind: "thud", dur: 0.2, freq: 180, freqEnd: 80, noise: 0.2 },
      ],
    },
    gain: 0.85, rateVar: 0.12,
  },

  // --- UI / ship events ---
  "ui.click": {
    synth: { kind: "tone", dur: 0.075, freq: 220, freqEnd: 160, type: "triangle", attack: 0.003, decay: 0.068, gain: 0.22 },
    bus: "ui", gain: 0.5, cooldownMs: 30,
  },
  "ui.select": {
    synth: { kind: "tone", dur: 0.1, freq: 180, freqEnd: 120, type: "triangle", attack: 0.004, decay: 0.09, gain: 0.24 },
    bus: "ui", gain: 0.55, cooldownMs: 40,
  },
  "ui.dock": {
    synth: { kind: "chord", dur: 0.55, root: 330, intervals: [0, 7, 12], type: "triangle", bright: 0.6, gain: 0.4 },
    bus: "ui", gain: 0.7,
  },
  "loot": {
    synth: { kind: "chord", dur: 0.4, root: 660, intervals: [0, 4, 7], type: "triangle", bright: 0.7, gain: 0.35 },
    bus: "ui", gain: 0.7,
  },
  "ship.undock": {
    synth: {
      kind: "layers", dur: 0.5, gain: 0.5, layers: [
        { kind: "noise", dur: 0.5, color: "white", freq: 600, freqEnd: 220, type: "lowpass", attack: 0.05, decay: 0.4, gain: 0.4 },
        { kind: "tone", dur: 0.4, freq: 320, freqEnd: 160, type: "sine", decay: 0.36, gain: 0.15 },
      ],
    },
    gain: 0.7,
  },
  "ship.warp": {
    synth: {
      kind: "layers", dur: 1.3, gain: 0.7, layers: [
        { kind: "zap", dur: 1.1, freq: 180, freqEnd: 1900, type: "sawtooth", gain: 0.35 },
        { kind: "noise", dur: 1.3, color: "white", freq: 400, freqEnd: 6000, type: "bandpass", q: 0.5, attack: 0.4, decay: 0.9, gain: 0.3 },
        { kind: "thud", dur: 0.6, freq: 120, freqEnd: 50, noise: 0.4 },
      ],
    },
    // Spatial: YOUR jump passes no position → plays 2D; a REMOTE jump passes the
    // ship's position → attenuates + pans with distance.
    spatial: true, refDist: 200, maxDist: 8000, rolloff: 0.8, gain: 0.8,
  },
};

// ----------------------------------------------------------------------------
// Ambience beds (loops; gain automated each frame by the bed manager)
// ----------------------------------------------------------------------------
export const BEDS: Record<string, BedDef> = {
  'bed.volcano': {
    synth:{kind:'layers',dur:5,loop:true,gain:.7,layers:[
      {kind:'noise',dur:5,color:'brown',freq:160,type:'lowpass',q:.4,loop:true,gain:.65},
      {kind:'noise',dur:5,color:'pink',freq:400,type:'lowpass',q:.4,loop:true,gain:.32},
    ]},gain:.5,spatial:true,refDist:220,maxDist:6000,rolloff:1,
  },
  "bed.wind": { synth: { kind: "noise", dur: 4, color: "pink", freq: 640, type: "lowpass", q: 0.4, loop: true, gain: 0.6 }, gain: 0.5 },
  // Layered so it reads as RAIN, not a single static hiss: an airy top "shh", a
  // mid body, a low rumble for weight, and a mid bandpass "spatter" band for the
  // droplet texture. Different loop lengths keep the layers from phase-locking
  // into an audible tone.
  "bed.rain": {
    synth: {
      kind: "layers", dur: 3.7, loop: true, gain: 0.6, layers: [
        { kind: "noise", dur: 3.7, color: "white", freq: 6200, type: "lowpass", q: 0.5, loop: true, gain: 0.34 },
        { kind: "noise", dur: 3.1, color: "pink", freq: 1500, type: "lowpass", q: 0.5, loop: true, gain: 0.3 },
        { kind: "noise", dur: 2.9, color: "brown", freq: 320, type: "lowpass", q: 0.6, loop: true, gain: 0.22 },
        { kind: "noise", dur: 3.3, color: "white", freq: 3000, type: "bandpass", q: 0.7, loop: true, gain: 0.26 },
      ],
    },
    gain: 0.55,
  },
  "bed.ocean": { synth: { kind: "noise", dur: 5, color: "brown", freq: 480, type: "lowpass", q: 0.5, loop: true, gain: 0.7 }, gain: 0.5 },
  "bed.underwater": { synth: { kind: "noise", dur: 4, color: "brown", freq: 300, type: "lowpass", q: 0.6, loop: true, gain: 0.7 }, gain: 0.6 },
  "bed.ship.engine": {
    synth: { kind: "chord", dur: 4, root: 55, intervals: [0, 7, 12], type: "sawtooth", lfoRate: 0.5, bright: 0.18, loop: true, gain: 0.5 },
    gain: 0.45,
  },
  "bed.vehicle.engine": {
    synth: {
      kind: "layers", dur: 3, loop: true, gain: 0.64, layers: [
        { kind: "tone", dur: 3, freq: 72, type: "sawtooth", loop: true, gain: 0.28, detune: 7 },
        { kind: "tone", dur: 3, freq: 144, type: "triangle", loop: true, gain: 0.13, vibrato: 5, vibratoDepth: 5 },
        { kind: "noise", dur: 3, color: "pink", freq: 360, type: "lowpass", q: 0.4, loop: true, gain: 0.11 },
      ],
    },
    gain: 0.52,
  },
  // FLIGHT BOOST = a WARM rumble. Pink noise (smooth, even per-octave — the warm
  // one; brown random-walks and reads as "low + crackly") in two low-passed bands
  // for body + low-mid warmth, over a soft triangle sub for weight (no vibrato →
  // no pulse). Looped; the bed manager swells gain with boost + speed, `rate`
  // shifts pitch (lower = heavier).
  "bed.ship.boost": {
    synth: {
      kind: "layers", dur: 4, loop: true, gain: 0.8, layers: [
        { kind: "noise", dur: 4, color: "pink", freq: 420, type: "lowpass", q: 0.5, loop: true, gain: 0.8 },
        { kind: "noise", dur: 4, color: "pink", freq: 180, type: "lowpass", q: 0.4, loop: true, gain: 0.5 },
        { kind: "tone", dur: 4, freq: 52, type: "triangle", loop: true, gain: 0.22 },
      ],
    },
    gain: 0.6,
    // POSITIONAL: placed at the ship so it attenuates + pans as the ship moves
    // (your own under chase-cam, and every remote ship). refDist large so your
    // own ship stays full at chase distance; fades over kilometres.
    spatial: true, refDist: 60, maxDist: 6000, rolloff: 0.9,
  },
  // SPACE-BAR PULSE drive = a BASSIER take on the boost rumble. Same warm pink
  // noise, but lower filter cutoffs (240/110 vs 420/180) and a deeper, stronger
  // sub (38Hz @ 0.34 vs 52Hz @ 0.22) → more weight under the high-speed surge.
  "bed.ship.pulse": {
    synth: {
      kind: "layers", dur: 4, loop: true, gain: 0.9, layers: [
        { kind: "noise", dur: 4, color: "pink", freq: 240, type: "lowpass", q: 0.5, loop: true, gain: 0.85 },
        { kind: "noise", dur: 4, color: "pink", freq: 110, type: "lowpass", q: 0.4, loop: true, gain: 0.6 },
        { kind: "tone", dur: 4, freq: 38, type: "triangle", loop: true, gain: 0.34 },
      ],
    },
    gain: 0.65,
    spatial: true, refDist: 70, maxDist: 7000, rolloff: 0.85, // louder drive carries a touch farther
  },
  // LOCAL player's jetpack — 2D (it's on your own back, at the listener).
  "bed.jet": {
    synth: {
      kind: "layers", dur: 2, loop: true, gain: 0.5, layers: [
        { kind: "noise", dur: 2, color: "white", freq: 1500, type: "bandpass", q: 0.5, loop: true, gain: 0.5 },
        { kind: "tone", dur: 2, freq: 140, type: "sawtooth", loop: true, gain: 0.12 },
      ],
    },
    gain: 0.4,
  },
  // REMOTE players' jetpacks — same sound, POSITIONAL (one keyed emitter per
  // remote) so a player jetting past you attenuates + pans with distance.
  "bed.jet.remote": {
    synth: {
      kind: "layers", dur: 2, loop: true, gain: 0.5, layers: [
        { kind: "noise", dur: 2, color: "white", freq: 1500, type: "bandpass", q: 0.5, loop: true, gain: 0.5 },
        { kind: "tone", dur: 2, freq: 140, type: "sawtooth", loop: true, gain: 0.12 },
      ],
    },
    gain: 0.5, spatial: true, refDist: 6, maxDist: 250, rolloff: 1.1,
  },
  "bed.mining": {
    synth: {
      kind: "layers", dur: 2, loop: true, gain: 0.5, layers: [
        { kind: "tone", dur: 2, freq: 220, type: "sawtooth", loop: true, gain: 0.2, vibrato: 9, vibratoDepth: 18 },
        { kind: "noise", dur: 2, color: "white", freq: 3000, type: "bandpass", q: 1.2, loop: true, gain: 0.15 },
      ],
    },
    spatial: true, refDist: 8, maxDist: 200, gain: 0.5,
  },
};

// ----------------------------------------------------------------------------
// Music — intermittent, authored files only. The director streams one track,
// then leaves a long silent gap before the next eligible file.
// ----------------------------------------------------------------------------
export const MUSIC_TRACKS: Record<string, MusicTrackDef> = {
  "music.flight.core": { url: "/audio/corespace.mp3", gain: 0.95 },
};

export const MUSIC_POOLS: Record<string, MusicPool> = {
  // Gaps are minutes — music is a treat, not a backing track. Tune freely.
  planet: { tracks: [], startGap: 20, minGap: 150, maxGap: 360, minPlay: 45, maxPlay: 95 },
  flight: { tracks: ["music.flight.core"], startGap: 35, minGap: 180, maxGap: 360, minPlay: 45, maxPlay: 95 },
  menu: { tracks: [], startGap: 6, minGap: 45, maxGap: 110, minPlay: 35, maxPlay: 70 },
};

/** Resolve synth-backed SFX/ambience for SoundBank. Music bypasses the bank. */
export function defOf(id: string): { url?: string; synth: SynthSpec } | undefined {
  if (id in SOUNDS) return SOUNDS[id];
  if (id in BEDS) return BEDS[id];
  return undefined;
}
